import { DataError } from "../../../core/errors/DataError";
import {
  OrderPriceAdjustmentOutcome,
  OrderPriceAdjustmentResultDto,
} from "../responses/OrderPriceAdjustmentDto";

const ERROR_OUTCOMES: OrderPriceAdjustmentOutcome[] = [
  "versionConflict",
  "notAdjustable",
  "paidOnOldLink",
  "stripeUnavailable",
];

// The API's 400 codes (OrderPriceAdjustmentResultDto.Error*). Exported so
// the dialog can translate the known ones; an unknown code still reaches
// the dialog as-is (see below) instead of collapsing into "saving failed".
export const VALIDATION_ERRORS = [
  "noChange",
  "invalidPrice",
  "lineNotAdjustable",
  "totalNotPositive",
  "noteTooLong",
  // Stripe answered the session create with a deterministic 4xx (amount
  // below its per-currency minimum, ...); nothing was written. The body's
  // `detail` is Stripe's own message and rides along (see below).
  "stripeRejected",
];

// The PUT price-adjustment route answers its non-OK cases with small JSON
// bodies: 409/502 { outcome, ... } and 400 { error, lineId? }. authFetch
// throws a DataError for every non-OK response; errorStatusFromBody keeps a
// body without `message` as the raw JSON text (409/502) or as the `error`
// string (400). This turns that DataError back into a result the dialog can
// branch on. Anything else (transport failure, 404 flag-off, an unknown
// body) returns null - the caller keeps throwing.
export function priceAdjustmentOutcomeFromError(
  e: unknown,
): OrderPriceAdjustmentResultDto | null {
  if (!(e instanceof DataError) || !e.errorStatus) return null;
  const { code, message } = e.errorStatus;
  if (code !== 409 && code !== 502 && code !== 400) return null;

  let parsed: unknown = undefined;
  if (typeof message === "string") {
    try {
      parsed = JSON.parse(message);
    } catch {
      parsed = undefined;
    }
  }
  const body =
    parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : undefined;

  if (code === 409 || code === 502) {
    const outcome = body?.outcome;
    if (
      typeof outcome === "string" &&
      (ERROR_OUTCOMES as string[]).includes(outcome)
    ) {
      return {
        outcome: outcome as OrderPriceAdjustmentOutcome,
        currentVersion:
          typeof body?.currentVersion === "number"
            ? body.currentVersion
            : undefined,
        reason: typeof body?.reason === "string" ? body.reason : undefined,
        currentStatus:
          typeof body?.currentStatus === "string"
            ? body.currentStatus
            : undefined,
      };
    }
    return null;
  }

  // 400: { error, lineId? } - the `error` string became the message, unless
  // the body also carried a message (then the raw text is what we parsed).
  // Any non-empty code/message is handed to the dialog as a validationError:
  // a code we do not know (a newer API) is shown verbatim rather than as the
  // generic "saving failed" (which would wrongly claim nothing was checked).
  // Only an empty 400 keeps throwing.
  const error =
    typeof body?.error === "string"
      ? body.error
      : typeof body?.message === "string"
        ? body.message
        : typeof message === "string"
          ? message
          : "";
  if (error.trim() === "") return null;
  // `detail` (stripeRejected): from the raw body when that survived as the
  // message, otherwise from the ErrorStatus (errorStatusFromBody keeps it
  // for the 400 while the flag is on). Empty = not sent.
  const detail =
    typeof body?.detail === "string"
      ? body.detail
      : typeof e.errorStatus.detail === "string"
        ? e.errorStatus.detail
        : "";
  return {
    outcome: "validationError",
    error,
    lineId: typeof body?.lineId === "number" ? body.lineId : undefined,
    detail: detail.trim() === "" ? undefined : detail,
  };
}
